import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import PublishBar from "../../components/admin/PublishBar";
import MediaUploader from "../../components/admin/MediaUploader";
import { contentWrite, createPreviewPR } from "../../services/content";

type ReferenceItem = {
  name: string;
  company: string;
  logo?: string;
};

const ReferencesAdmin: React.FC = () => {
  const [items, setItems] = React.useState<ReferenceItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod",
  );
  const [commitMsg, setCommitMsg] = React.useState(
    "chore(references): update",
  );
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  const previewBranchRef = React.useRef<string>("");

  const getActiveBranch = () => {
    if (publishMode !== "preview") return undefined;
    const normalized = branchName.trim();
    if (normalized) return normalized;
    if (!previewBranchRef.current) {
      previewBranchRef.current = `preview/references-${Date.now()}`;
    }
    return previewBranchRef.current;
  };

  const activeBranch = getActiveBranch();

  const [newName, setNewName] = React.useState("");
  const [newCompany, setNewCompany] = React.useState("");
  const [newLogo, setNewLogo] = React.useState<string>("");
  const [editIndex, setEditIndex] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editCompany, setEditCompany] = React.useState("");
  const [editLogo, setEditLogo] = React.useState<string>("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/data/company/references.json", {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Referanslar okunamadı");
      const json = await res.json();
      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (e: any) {
      setError(e?.message || "Referanslar okunamadı");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const saveAll = async (next: ReferenceItem[]) => {
    setLoading(true);
    setError("");
    try {
      const branch = activeBranch;

      await contentWrite(
        "data/company/references.json",
        JSON.stringify({ items: next }, null, 2),
        commitMsg,
        branch,
      );

      if (branch) {
        const pr = await createPreviewPR(branch, "References Update");
        setPrUrl(pr.url);
      }

      setItems(next);
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const addReference = () => {
    if (!newCompany.trim()) {
      setError("Firma adı gerekli");
      return;
    }
    const next: ReferenceItem[] = [
      ...items,
      {
        name: newName.trim(),
        company: newCompany.trim(),
        logo: newLogo.trim() || undefined,
      },
    ];
    setItems(next);
    setNewName("");
    setNewCompany("");
    setNewLogo("");
  };

  const beginEdit = (idx: number) => {
    setEditIndex(idx);
    setEditName(items[idx].name);
    setEditCompany(items[idx].company);
    setEditLogo(items[idx].logo || "");
  };

  const commitEdit = () => {
    if (editIndex === null) return;
    if (!editCompany.trim()) {
      setError("Firma adı gerekli");
      return;
    }
    const next = items.map((it, i) =>
      i === editIndex
        ? {
            name: editName.trim(),
            company: editCompany.trim(),
            logo: editLogo.trim() || undefined,
          }
        : it,
    );
    setItems(next);
    setEditIndex(null);
  };

  const remove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const arr = [...items];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[ni];
    arr[ni] = tmp;
    setItems(arr);
  };

  const publish = async () => {
    if (!items.length) {
      // Yayına alınacak veri yoksa yine de sessizce çıkabiliriz
      // veya bilgi mesajı gösterebiliriz; şimdilik sessiz.
    }
    await saveAll(items);
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Referans Yönetimi
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: Referanslar ana sayfadaki "Referanslarımız" bölümünde
          görüntülenir. Sıralama, kartların gösterim sırasını belirler.
        </Alert>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/references`}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={publish}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Yeni Referans Ekle
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Firma Adı"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                fullWidth
              />
              <TextField
                label="İlgili Kişi (opsiyonel)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
              />
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={addReference}
                disabled={loading}
              >
                Ekle
              </Button>
            </Stack>
            <Box sx={{ mt: 2 }}>
              <MediaUploader
                title="Logo (opsiyonel)"
                accept="image/*"
                multiple={false}
                destPrefix="data/company/uploads"
                branch={activeBranch}
                onUploaded={(paths) => setNewLogo(paths[0] || "")}
              />
              {newLogo && (
                <Chip
                  label={newLogo.split("/").pop()}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          {items.map((it, idx) => (
            <Grid item xs={12} key={`${it.company}-${idx}`}>
              <Card>
                <CardContent>
                  {editIndex === idx ? (
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          label="Firma Adı"
                          value={editCompany}
                          onChange={(e) => setEditCompany(e.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="İlgili Kişi (opsiyonel)"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          fullWidth
                        />
                        <Button
                          startIcon={<SaveIcon />}
                          variant="contained"
                          onClick={commitEdit}
                          disabled={loading}
                        >
                          Kaydet
                        </Button>
                      </Stack>
                      <Box>
                        <MediaUploader
                          title="Logo (opsiyonel)"
                          accept="image/*"
                          multiple={false}
                          destPrefix="data/company/uploads"
                          branch={activeBranch}
                          onUploaded={(paths) => setEditLogo(paths[0] || "")}
                        />
                        {(editLogo || it.logo) && (
                          <Chip
                            label={(editLogo || it.logo || "").split("/").pop()}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </Stack>
                  ) : (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems="center"
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {it.company}
                        </Typography>
                        {it.name && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            {it.name}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => move(idx, 1)}
                          disabled={idx === items.length - 1}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => beginEdit(idx)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(idx)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ReferencesAdmin;
